import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Accordion, AccordionTab } from "primereact/accordion";
import {
  ComplaintSubNatures,
  NatureComplaintDTO,
  StepCallBack,
} from "@modules/plaintes/plaintes.model.ts";
import { ComplaintService } from "@modules/plaintes/plaintes.service.ts";
import Spinner from "@shared/Spinner.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  natureComplaintSelectedList,
  selectedReasons,
  setNatureComplaintSelectedList,
  setSelectedReasons,
  setStepReasonValue,
  setSubNatureComplaintSelectedList,
  stepReasonValue,
  subNatureComplaintSelectedList,
} from "@core/slice/ComplaintSlice.ts";
import { InputText } from "primereact/inputtext";
import {
  DynamicOptionCategory,
  DynamicOptionPostDTO,
  BACKEND_SUPPORTED_CATEGORIES,
} from "@core/modeles/dynamicOption.model.ts";
import { createDynamicOptions, fetchStandardDynamicOptions } from "@core/actions/dynamicOptionAction.ts";
import {
  incrementDynamicOption,
  makeSelectStandardLabels,
  STANDARD_THRESHOLD,
  makeCountKey,
} from "@core/slice/dynamicOptionsCountSlice.ts";

const ComplaintType: React.FC<StepCallBack> = ({ onNext, onPrevious }) => {
  const dispatch = useAppDispatch();
  const stepReason = useAppSelector(stepReasonValue);
  const selectedReason = useAppSelector(selectedReasons);
  const natureComplaintSelected = useAppSelector(natureComplaintSelectedList);
  const subNatureComplaintSelected = useAppSelector(subNatureComplaintSelectedList);

  // ── Options standard DynamicOption ───────────────────────────────────────
  const selectClusterStandard = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.CLUSTER),
    []
  );
  const standardClusterLabels = useAppSelector(selectClusterStandard);
  const dynamicCounts = useAppSelector(
    (state: any) => state.dynamicOptionsCount?.counts ?? {}
  );
  // ─────────────────────────────────────────────────────────────────────────

  const [natureComplaintList, setNatureComplaintList] =
    useState<NatureComplaintDTO[]>();
  const complaintServiceRef = useRef(new ComplaintService());

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({ natureOfComplaint: "" });
  const [errorOther, setErrorOther] = useState<Record<string, string>>({});
  const [idSubSelected, setIdSubSelected] = useState<ComplaintSubNatures[]>(
    () => stepReason?.stepFour?.complaintSubNatures ?? []
  );
  const [chipSelectedSubs, setChipSelectedSubs] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchNatureComplaint();
    // Fetch standard labels pour les sous-types "Autres" (catégorie CLUSTER)
    if (BACKEND_SUPPORTED_CATEGORIES.has(DynamicOptionCategory.CLUSTER)) {
      dispatch(fetchStandardDynamicOptions(DynamicOptionCategory.CLUSTER));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const filterSubSelected = subNatureComplaintSelected?.filter(
      (val) => val.isCustom === true
    );
    idSubSelected.forEach(({ natureId, subnatureId, additionalInfo }) => {
      if (
        (!additionalInfo || additionalInfo.trim() === "") &&
        filterSubSelected.map((val) => val.id).includes(subnatureId)
      ) {
        newErrors[natureId] = "Ce champ est requis";
      }
    });
    setErrorOther(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log("[ComplaintType] handleValidate –", isValid ? "✅ valide" : "❌ invalide", newErrors);
    return isValid;
  };

  function handleNextStep() {
    console.log("[ComplaintType] handleNextStep – subNatures courants:", idSubSelected);
    if (
      subNatureComplaintSelected?.length === 0 ||
      !subNatureComplaintSelected?.length
    ) {
      console.warn("[ComplaintType] handleNextStep – aucune sous-nature sélectionnée");
      setErrors((prevErrors) => ({
        ...prevErrors,
        natureOfComplaint: "Veuillez cocher la nature de la plainte",
      }));
      return;
    }
    if (!handleValidate()) {
      console.log("[ComplaintType] handleNextStep – erreurs champ Autres:", errorOther);
      return;
    }
    setErrors((prevErrors) => ({ ...prevErrors, natureOfComplaint: "" }));

    // ── POST /dynamic-option pour les sous-types "Autres" (CLUSTER) ──────────
    {
      const rawWithAdditional = idSubSelected.filter((sub) => sub.additionalInfo?.trim());
      const seenKeys = new Set<string>();
      const dynamicPayload: DynamicOptionPostDTO[] = [];
      for (const sub of rawWithAdditional) {
        const label = sub.additionalInfo!.trim();
        const category = DynamicOptionCategory.CLUSTER;
        const dedupKey = `${category}__${label.toLowerCase()}`;
        if (seenKeys.has(dedupKey)) continue;
        seenKeys.add(dedupKey);
        // Comptage frontend
        dispatch(incrementDynamicOption({ category, label }));
        const currentCount = (dynamicCounts[makeCountKey(category, label)] ?? 0) + 1;
        const remaining = Math.max(0, STANDARD_THRESHOLD - currentCount);
        if (remaining === 0) {
          console.log(`[ComplaintType] ✅ "${label}" [${currentCount}/${STANDARD_THRESHOLD}] → VISIBLE dans suggestions`);
        } else {
          console.log(`[ComplaintType] ⏳ "${label}" [${currentCount}/${STANDARD_THRESHOLD}] → encore ${remaining} soumission(s)`);
        }
        if (BACKEND_SUPPORTED_CATEGORIES.has(category)) {
          dynamicPayload.push({ labelOriginal: label, category, natureId: sub.subnatureId });
        } else {
          console.warn(`[ComplaintType] ⏭️ POST ignoré pour "${label}" — CLUSTER non supporté par le backend`);
        }
      }
      if (dynamicPayload.length > 0) {
        console.log("[ComplaintType] POST /dynamic-option →", dynamicPayload);
        dispatch(createDynamicOptions(dynamicPayload));
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    if (stepReason) {
      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepFour: {
            natureComplaintSelectedList: natureComplaintSelected,
            subNatureComplaintSelectedList: subNatureComplaintSelected,
            complaintSubNatures: idSubSelected,
          },
        })
      );
    }
    console.log("[ComplaintType] handleNextStep – dispatch OK, passage étape suivante");
    onNext();
  }

  function handlePreviousStep() {
    if (onPrevious) {
      onPrevious();
    }
  }

  const handleReasonToggle = (
    selectedSubNature: number,
    parentNature: NatureComplaintDTO
  ) => {
    const isSelected = selectedReason.includes(selectedSubNature);
    console.log(`[ComplaintType] handleReasonToggle – ${isSelected ? "décocher" : "cocher"} subnature id=${selectedSubNature} (nature: ${parentNature.name})`);

    const updatedReasons = isSelected
      ? selectedReason.filter((reason) => reason !== selectedSubNature)
      : [...selectedReason, selectedSubNature];

    dispatch(setSelectedReasons(updatedReasons));

    const updatedSubNatures = isSelected
      ? subNatureComplaintSelected.filter(
          (subNature) => subNature.id !== selectedSubNature
        )
      : [
          ...subNatureComplaintSelected,
          parentNature.subNatures.find(
            (sub) => sub.id === selectedSubNature
          )!,
        ];
    dispatch(setSubNatureComplaintSelectedList(updatedSubNatures));

    const isAnySubNatureSelected = parentNature.subNatures.some((subNature) =>
      updatedReasons.includes(subNature.id)
    );
    const alreadySelected = natureComplaintSelected.some(
      (nature) => nature.id === parentNature.id
    );
    let updatedNatureComplaintList = natureComplaintSelected;
    if (isAnySubNatureSelected && !alreadySelected) {
      updatedNatureComplaintList = [...natureComplaintSelected, parentNature];
    } else if (!isAnySubNatureSelected && alreadySelected) {
      updatedNatureComplaintList = natureComplaintSelected.filter(
        (nature) => nature.id !== parentNature.id
      );
    }
    dispatch(setNatureComplaintSelectedList(updatedNatureComplaintList));
    return updatedReasons;
  };

  const fetchNatureComplaint = async () => {
    try {
      const data = await complaintServiceRef.current.getAllNatureComplaint();
      const motifComplaint = data.filter((item) => item.type === "TYPE");
      console.log(`[ComplaintType] fetchNatureComplaint – ${motifComplaint.length} type(s) chargé(s):`, motifComplaint.map((n) => n.name));
      setNatureComplaintList(motifComplaint);
      setIsLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement des nature de plainte :", error);
      setIsLoading(false);
    }
  };

  return (
    <div className="column gap-[20px] h-12rem card-stepper">
      <div className="stitle">Type de la plainte</div>
      {isLoading ? (
        <Spinner />
      ) : (
        <Accordion multiple className="mb-4">
          {natureComplaintList?.map((nature) => (
            <AccordionTab key={nature.id} header={nature.name}>
              <div className="p-2 space-y-2">
                {/* ── Chips suggestions DynamicOption pour sous-types isCustom ── */}
                {(() => {
                  const customSub = nature.subNatures.find((s) => s.isCustom);
                  if (!customSub || standardClusterLabels.length === 0) return null;
                  const isCustomChecked = selectedReason.includes(customSub.id);
                  const customEntry = idSubSelected.find((a) => a.subnatureId === customSub.id);
                  return (
                    <div className="flex flex-col gap-y-1 mb-2">
                      {standardClusterLabels.map((chipLabel) => {
                        const isActive = isCustomChecked && customEntry?.additionalInfo === chipLabel;
                        const chipId = `sugg-type-${customSub.id}-${chipLabel}`;
                        return (
                          <div key={chipLabel} className="flex items-center">
                            <Checkbox
                              inputId={chipId}
                              checked={isActive}
                              onChange={() => {
                                setErrorOther({});
                                setErrors({ natureOfComplaint: "" });
                                if (isActive) {
                                  console.log(`[ComplaintType] chip désélectionné "${chipLabel}" (nature: ${nature.name})`);
                                  handleReasonToggle(customSub.id, nature);
                                  setIdSubSelected((prev) =>
                                    prev.filter((sub) => sub.subnatureId !== customSub.id)
                                  );
                                  setChipSelectedSubs((prev) => {
                                    const next = new Set(prev);
                                    next.delete(customSub.id);
                                    return next;
                                  });
                                } else {
                                  console.log(`[ComplaintType] chip sélectionné "${chipLabel}" (nature: ${nature.name})`);
                                  if (!isCustomChecked) handleReasonToggle(customSub.id, nature);
                                  setIdSubSelected((prev) => {
                                    const exists = prev.find((a) => a.subnatureId === customSub.id);
                                    if (exists) {
                                      return prev.map((sub) =>
                                        sub.subnatureId === customSub.id
                                          ? { ...sub, additionalInfo: chipLabel }
                                          : sub
                                      );
                                    }
                                    return [...prev, { natureId: nature.id, subnatureId: customSub.id, additionalInfo: chipLabel }];
                                  });
                                  setChipSelectedSubs((prev) => new Set([...prev, customSub.id]));
                                }
                              }}
                            />
                            <label htmlFor={chipId} className="ml-2 cursor-pointer select-none">
                              {chipLabel}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                {/* ── Liste normale des sous-natures ── */}
                {[
                  ...nature.subNatures.filter((item) => !item.isCustom),
                  ...nature.subNatures.filter((item) => item.isCustom),
                ].map((item) => {
                  if (item.isCustom && chipSelectedSubs.has(item.id)) return null;
                  const isIdSelected = idSubSelected.find((a) => a.subnatureId === item.id);
                  return (
                    <div key={item.id}>
                      <div className="flex items-center">
                        <Checkbox
                          inputId={`${item.id}`}
                          checked={selectedReason.includes(item.id)}
                          onChange={() => {
                            setErrorOther({});
                            setErrors({ natureOfComplaint: "" });
                            handleReasonToggle(item.id, nature);
                            if (item.isCustom) {
                              if (isIdSelected !== undefined) {
                                setIdSubSelected((prev) =>
                                  prev.filter((a) => a.subnatureId !== item.id)
                                );
                              } else {
                                setIdSubSelected((prev) => [
                                  ...prev,
                                  { natureId: nature.id, subnatureId: item.id, additionalInfo: "" },
                                ]);
                              }
                            }
                          }}
                        />
                        <label htmlFor={`${item.id}`} className="ml-2">
                          {item.description}
                        </label>
                      </div>
                      {/* Champ texte libre si isCustom coché */}
                      {item.isCustom &&
                        selectedReason.includes(item.id) &&
                        isIdSelected !== undefined && (
                          <div className="flex flex-col w-full mt-1 ml-6">
                            <label
                              htmlFor={`${item.id}-custom`}
                              className="text-sm font-medium"
                            >
                              Veuillez préciser :
                            </label>
                            <InputText
                              id={`${item.id}-custom`}
                              placeholder="Saisir ici..."
                              className="w-1/2 mt-1"
                              value={isIdSelected?.additionalInfo ?? ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                console.log(`[ComplaintType] saisie "Autres" (subnature id=${item.id}) → "${newValue}"`);
                                setChipSelectedSubs((prev) => {
                                  const next = new Set(prev);
                                  next.delete(item.id);
                                  return next;
                                });
                                setIdSubSelected((prev) =>
                                  prev.map((sub) =>
                                    sub.subnatureId === item.id
                                      ? { ...sub, additionalInfo: newValue }
                                      : sub
                                  )
                                );
                              }}
                            />
                            {errorOther[nature.id] && (
                              <small className="text-red-600 mt-1">
                                {errorOther[nature.id]}
                              </small>
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </AccordionTab>
          ))}
        </Accordion>
      )}
      {errors.natureOfComplaint && (
        <small className="text-red-600">{errors.natureOfComplaint}</small>
      )}
      <div className="flex pt-4 gap-3 justify-content-end mr-6">
        <div className="flex pt-4 justify-content-end">
          <Button
            label="Précédent"
            className="btn btn-secondary w-[120px]"
            onClick={handlePreviousStep}
          />
        </div>
        <div className="flex pt-4 justify-content-end">
          <Button
            label="Suivant"
            className="btn btn-primary w-[120px]"
            onClick={handleNextStep}
          />
        </div>
      </div>
    </div>
  );
};

export default ComplaintType;
